import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import { ANGLES_MODELS } from '../constants/models';
import Icons from '../components/Icons';
import toast from 'react-hot-toast';
import Lightbox from '../components/Lightbox';
import { groupItemsByDate } from '../utils/grouping';
import { compressImage } from '../utils/imageCompression';
import MediaLibraryModal from '../components/MediaLibraryModal';

import FuturisticSlider from '../components/FuturisticSlider';
import { confirmAlert } from '../utils/confirmAlert';

const CINEMATIC_PRESETS = [
  { label: "Default", x: 0, y: 0, d: 1 },
  { label: "High Angle", x: 45, y: 0, d: 1.5 },
  { label: "Low Angle", x: -30, y: 0, d: 1 },
  { label: "Dutch Tilt", x: 15, y: 25, d: 1.2 },
  { label: "Over Shoulder", x: -5, y: 45, d: 2 },
  { label: "Bird's Eye", x: 80, y: 0, d: 3 },
  { label: "Close Up", x: 0, y: 0, d: 5 },
];

export default function AnglesPage({ onShowAuth }) {
  const { user, token } = useAuth();
  const { credits, setCredits, plan, items = [], pendingJobs = [], setPendingJobs, refreshItems, removeItem } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();

  const [model, setModel] = useState('qwen-lora');
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);

  // Angle specific states
  const [sourceImage, setSourceImage] = useState(null);
  const [libOpen, setLibOpen] = useState(false);

  // 3D Sliders
  const [rotateX, setRotateX] = useState(0); // Up-Down / Vertical
  const [rotateY, setRotateY] = useState(0); // Left-Right / Horizontal
  const [distance, setDistance] = useState(1); // Scale/Distance
  const [wideAngle, setWideAngle] = useState(false); // Wide-Angle Lens Toggle
  const [prompt, setPrompt] = useState(''); // Optional prompt for Qwen

  const [lbItem, setLbItem] = useState(null);
  const fileRef = useRef();
  const genLockRef = useRef(false);

  useEffect(() => {
    const st = location.state;
    if (!st || !st.sourceUrl) return;

    const setupIncoming = async () => {
      try {
        // Store as an asset-like object so safeImgSrc uses fileUrl directly
        // and generateAngles will send it as sourceImageUrl (string path)
        const API_BASE = process.env.REACT_APP_API_URL || '/api';
        const proxyUrl = st.sourceUrl.startsWith('http')
          ? `${API_BASE}/proxy-image?url=${encodeURIComponent(st.sourceUrl)}`
          : st.sourceUrl;

        // Verify the URL is reachable before accepting it
        const resp = await fetch(proxyUrl, { method: 'HEAD' }).catch(() => null);
        if (resp && !resp.ok) {
          // Fallback: try a full GET and create a local blob URL
          const getResp = await fetch(proxyUrl);
          if (!getResp.ok) throw new Error('Failed to fetch image');
          const blob = await getResp.blob();
          const file = new File([blob], 'reference.jpg', { type: blob.type || 'image/jpeg' });
          const compressed = await compressImage(file);
          setSourceImage(compressed);
        } else {
          // Use the proxy URL directly as a fileUrl asset object
          setSourceImage({ fileUrl: proxyUrl });
        }

        navigate(location.pathname, { replace: true, state: {} });
      } catch (e) {
        toast.error('Failed to load reference image');
      }
    };
    setupIncoming();
  }, [location.state, location.pathname, navigate]);

  const currentModel = ANGLES_MODELS.find(m => m.id === model) || ANGLES_MODELS[0];
  const cost = currentModel?.cost || 10;

  const isModelLocked = useCallback((id) => {
    return false;
  }, []);

  const handleModelChange = (newModel) => {
    setModel(newModel);
    setShowModelPicker(false);
  };

  const applyPreset = (preset) => {
    let px = preset.x;
    let py = preset.y;
    let pd = preset.d;

    if (model === 'qwen-lora') {
      if (preset.label === 'High Angle') px = 1;
      else if (preset.label === 'Low Angle') px = -1;
      else if (preset.label === 'Dutch Tilt') px = 0.3;
      else if (preset.label === "Bird's Eye") px = 1;
      else px = 0;

      if (preset.label === 'Dutch Tilt') py = 25;
      else if (preset.label === 'Over Shoulder') py = 45;
      else py = 0;
    }

    setRotateX(px);
    setRotateY(py);
    setDistance(pd);
  };

  // Tracking logic for immersive 3D preview
  const isDragging = useRef(false);
  const lastMousePos = useRef({ x: 0, y: 0 });
  const dragRot = useRef({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const canvasRef = useRef(null);

  const handlePointerDown = (e) => {
    isDragging.current = true;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
    dragRot.current = { x: rotateX, y: rotateY };
    if (canvasRef.current) {
      canvasRef.current.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e) => {
    if (!isDragging.current) return;
    const deltaX = e.clientX - lastMousePos.current.x;
    const deltaY = e.clientY - lastMousePos.current.y;
    lastMousePos.current = { x: e.clientX, y: e.clientY };

    dragRot.current.y -= deltaX * 0.3;
    let rawY = dragRot.current.y;
    rawY = Math.max(-90, Math.min(90, rawY));
    setRotateY(Math.round(rawY / 15) * 15);

    dragRot.current.x += deltaY * 0.005;
    let rawX = Math.max(-1, Math.min(1, dragRot.current.x));
    // Round to 1 decimal to avoid floating-point drift (e.g. 0.32747474)
    setRotateX(parseFloat((Math.round(rawX / 0.1) * 0.1).toFixed(1)));
  };

  const handlePointerUp = (e) => {
    isDragging.current = false;
    if (canvasRef.current && e && e.pointerId) {
      try {
        canvasRef.current.releasePointerCapture(e.pointerId);
      } catch (err) { }
    }
  };

  const handleWheel = (e) => {
    if (!isHovering) return;
    setDistance(prev => {
      let newD = prev + e.deltaY * 0.005;
      return Math.max(1, Math.min(10, newD));
    });
  };

  const handleGen = async () => {
    if (genLockRef.current) return;
    if (!user) { if (onShowAuth) onShowAuth(); return; }
    if (!sourceImage) { toast.error('Upload a source image first'); return; }
    if (credits < cost) { toast.error('Not enough credits'); navigate('/subscription'); return; }
    genLockRef.current = true;

    const pendingId = `angle_pend_${Date.now()}`;
    const newJob = { id: pendingId, type: 'angle', prompt: 'Generating Angle...', model: currentModel?.label || model };
    if (setPendingJobs) setPendingJobs(p => [newJob, ...p]);

    try {
      const api = await import('../services/api');
      // Determine what to send as sourceImage
      const srcImg = sourceImage?.fileUrl || sourceImage;
      const payload = {
        sourceImage: srcImg,
        model,
        horizontal_angle: rotateY,
        vertical_angle: rotateX,
        distance,
        wideAngle: wideAngle ? true : undefined,
      };

      const res = await api.generateAngles(token, payload);
      if (res.success) {
        // Update credits immediately from response — no extra Firebase call
        if (res.creditsRemaining !== undefined && setCredits) {
          setCredits(res.creditsRemaining);
        }
        if (refreshItems) refreshItems(true);
        toast.success('Angle Generated!');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Generation failed');
    } finally {
      if (setPendingJobs) setPendingJobs(p => p.filter(j => j.id !== pendingId));
      genLockRef.current = false;
    }
  };

  const recentImages = items
    .filter(i => i.type !== 'video' && i.anglePrompt || i.type === 'angle' || i.type === 'image')
    .sort((a, b) => {
      const ta = a.createdAt?.seconds || a.createdAt?._seconds || 0;
      const tb = b.createdAt?.seconds || b.createdAt?._seconds || 0;
      return tb - ta;
    })
    .slice(0, 12);

  const localJobs = pendingJobs.filter(j => j.type === 'angle' || j.type === 'image');

  const handleDeleteItem = async (id) => {
    const isConfirmed = await confirmAlert({
      title: 'Delete Creation?',
      message: 'Are you sure you want to delete this creation? This action cannot be undone.'
    });
    if (!isConfirmed) return;
    try {
      const api = await import('../services/api');
      await api.deleteGeneration(token, id);
      removeItem(id);
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const previewRotateX = model === 'qwen-lora' ? rotateX * 60 : rotateX; // map -1/1 to -60/60 roughly for visual
  const previewScale = distance > 1 ? 1 + (distance - 1) * 0.05 : distance; // Dampen scale significantly for visual sanity

  const imgStyle = {
    transform: `scale(${previewScale})`,
    transition: 'transform 0.15s ease-out',
    boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.6)',
    borderRadius: '12px',
    maxWidth: '180px',
    maxHeight: '180px',
    objectFit: 'contain',
    userSelect: 'none',
    pointerEvents: 'none'
  };

  // Safe preview URL — works for both library assets (have .fileUrl) and freshly
  // compressed File objects (need createObjectURL). Avoids crash when sourceImage
  // is an asset object that has no .type / is not a Blob.
  const safeImgSrc = (img) => {
    if (!img) return '';
    if (img.fileUrl) return img.fileUrl;
    try { return URL.createObjectURL(img); } catch { return ''; }
  };

  return (
    <>
      {/* ── Model Picker Popup ── */}
      {showModelPicker && (
        <div className="mp-overlay" onClick={() => setShowModelPicker(false)}>
          <div className="mp-popup" onClick={e => e.stopPropagation()}>
            <div className="mp-header">
              <div className="mp-title">Select Model</div>
              <button className="mc-btn" onClick={() => setShowModelPicker(false)}>
                <Icons.X sz={14} />
              </button>
            </div>
            <div className="mp-body">
              {ANGLES_MODELS.map(m => {
                const locked = isModelLocked(m.id);
                return (
                  <button
                    key={m.id}
                    className={`mp-item ${model === m.id ? 'on' : ''}`}
                    onClick={() => handleModelChange(m.id)}
                  >
                    {m.icon && Icons[m.icon] ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, marginRight: 10, color: m.color }}>
                        {React.createElement(Icons[m.icon], { sz: 18, color: m.color })}
                      </div>
                    ) : <div className="mp-dot" style={{ background: m.color }} />}
                    <div className="mp-info">
                      <div className="mp-name">
                        {m.label}
                      </div>
                    </div>
                    {model === m.id && (
                      <div className="mp-check">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      <button className={`mobile-fab ${showMobilePreview ? 'on' : ''}`} onClick={() => setShowMobilePreview(!showMobilePreview)}>
        <Icons.Orbit sz={22} />
        <span>{showMobilePreview ? 'Close' : 'Preview'}</span>
      </button>

      {/* ── Settings Panel ── */}
      <section className="panel">
        <div className="phead"><Icons.Orbit sz={16} /> Different Angles</div>

        {/* Model Selection */}
        <div className="psec">
          <div className="slbl">Model</div>
          <button className="mp-trigger" onClick={() => setShowModelPicker(true)}>
            <div className="mp-trigger-left">
              {currentModel?.icon && Icons[currentModel.icon] ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, marginRight: 10, color: currentModel.color }}>
                  {React.createElement(Icons[currentModel.icon], { sz: 20, color: currentModel.color })}
                </div>
              ) : <div className="mp-dot" style={{ background: currentModel?.color }} />}
              <div>
                <div className="mp-trigger-name">{currentModel?.label}</div>
              </div>
            </div>
            <div className="mp-trigger-right">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            </div>
          </button>
        </div>

        {/* ── Inline Virtual Environment Preview ── */}
        <div className="psec">
          <div className="slbl">Visual Environment</div>
          <div
            ref={canvasRef}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: 240, position: 'relative', overflow: 'hidden',
              background: sourceImage ? 'radial-gradient(circle at center, #1a1a2e 0%, #0b0b14 100%)' : 'rgba(0,0,0,0.2)',
              borderRadius: 14, marginBottom: 0,
              cursor: sourceImage ? (isDragging.current ? 'grabbing' : 'grab') : 'default',
              border: sourceImage ? '1px solid rgba(56,189,248,0.15)' : '1px solid rgba(255,255,255,0.05)',
              touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none'
            }}
            onPointerDown={sourceImage ? handlePointerDown : undefined}
            onPointerMove={sourceImage ? handlePointerMove : undefined}
            onPointerUp={sourceImage ? handlePointerUp : undefined}
            onPointerCancel={sourceImage ? handlePointerUp : undefined}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => { setIsHovering(false); handlePointerUp(); }}
            onWheel={sourceImage ? handleWheel : undefined}
          >
            {sourceImage ? (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, position: 'relative' }}>
                {/* Source image with zoom scale */}
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                  <img src={safeImgSrc(sourceImage)} alt="Preview" style={imgStyle} draggable="false" />
                </div>

                {/* 3D Camera Orbital Rig */}
                <div style={{ position: 'absolute', top: '50%', left: '50%', perspective: '1000px', zIndex: 10, pointerEvents: 'none', width: 0, height: 0 }}>
                  <div style={{
                    position: 'absolute', top: 0, left: 0,
                    transformStyle: 'preserve-3d',
                    transform: `rotateX(${-previewRotateX}deg) rotateY(${-rotateY}deg)`,
                    transition: isDragging.current ? 'none' : 'transform 0.12s ease-out'
                  }}>
                    {/* Vertical meridian rings — themed gradient colors */}
                    {[
                      { deg: 0,   color: 'rgba(56,189,248,0.18)' },
                      { deg: 30,  color: 'rgba(99,102,241,0.14)' },
                      { deg: 60,  color: 'rgba(139,92,246,0.14)' },
                      { deg: 90,  color: 'rgba(56,189,248,0.18)' },
                      { deg: 120, color: 'rgba(99,102,241,0.12)' },
                      { deg: 150, color: 'rgba(139,92,246,0.12)' },
                    ].map(({ deg, color }) => (
                      <div key={deg} style={{
                        position: 'absolute', top: -110, left: -110, width: 220, height: 220,
                        border: `1px solid ${color}`, borderRadius: '50%',
                        transform: `rotateY(${deg}deg)`
                      }} />
                    ))}

                    {/* Horizontal latitude rings */}
                    {[
                      { tilt: 90,  color: 'rgba(56,189,248,0.30)', width: '1px' },   /* equator — slightly bolder */
                      { tilt: 60,  color: 'rgba(99,102,241,0.18)', width: '0.5px' },
                      { tilt: 120, color: 'rgba(99,102,241,0.18)', width: '0.5px' },
                    ].map(({ tilt, color, width }) => (
                      <div key={tilt} style={{
                        position: 'absolute', top: -110, left: -110, width: 220, height: 220,
                        border: `${width} solid ${color}`, borderRadius: '50%',
                        transform: `rotateX(${tilt}deg)`
                      }} />
                    ))}

                    {/* 3D Camera body marker */}
                    <div style={{
                      position: 'absolute',
                      top: -20, left: -20, width: 40, height: 40,
                      background: 'rgba(10,10,20,0.92)',
                      border: '1.5px solid #38bdf8',
                      borderRadius: 10,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 0 20px rgba(56,189,248,0.5), 0 0 6px rgba(56,189,248,0.3)',
                      transform: `translateZ(110px) rotateY(180deg)`,
                      backdropFilter: 'blur(6px)'
                    }}>
                      {/* 3D Camera SVG */}
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M23 7l-7 5 7 5V7z" fill="rgba(56,189,248,0.15)" />
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" fill="rgba(56,189,248,0.08)" />
                        <circle cx="8.5" cy="12" r="2.5" fill="rgba(56,189,248,0.2)" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* HUD readout */}
                <div style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(0,0,0,0.55)', padding: '7px 12px', borderRadius: 8, backdropFilter: 'blur(8px)', border: '1px solid rgba(56,189,248,0.15)', color: '#38bdf8', fontFamily: 'monospace', fontSize: 10, display: 'flex', flexDirection: 'column', gap: 3, zIndex: 11, pointerEvents: 'none' }}>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, marginBottom: 2, letterSpacing: 1, fontSize: 9 }}>CAMERA</div>
                  <div style={{ display: 'flex', gap: 12 }}><span>AZ</span><b>{Math.round(rotateY)}°</b></div>
                  <div style={{ display: 'flex', gap: 12 }}><span>EL</span><b>{rotateX.toFixed(1)}</b></div>
                  <div style={{ display: 'flex', gap: 12 }}><span>D</span><b>{distance.toFixed(1)}x</b></div>
                </div>

                {/* Drag hint */}
                <div style={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.6)', fontSize: 9, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,0,0,0.45)', padding: '4px 9px', borderRadius: 14, zIndex: 11, pointerEvents: 'none' }}>
                  <div style={{ width: 5, height: 5, background: isDragging.current ? '#38bdf8' : 'rgba(255,255,255,0.35)', borderRadius: '50%', boxShadow: isDragging.current ? '0 0 6px #38bdf8' : 'none' }} />
                  {isDragging.current ? 'TRACKING' : 'DRAG TO PAN'}
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--t3)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3 }}>
                  <path d="M23 7l-7 5 7 5V7z" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
                <span style={{ fontSize: 11 }}>Upload an image to open Virtual Environment</span>
              </div>
            )}
          </div>
        </div>

        {/* Source Image Upload */}
        <div className="psec">
          <div className="slbl">Source Image</div>
          <div className={`uzone sm ${sourceImage ? 'on' : ''}`} onClick={() => setLibOpen(true)} style={{ padding: '20px 10px' }}
            onDragOver={e => e.preventDefault()}
            onDrop={async e => {
              e.preventDefault();
              if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                const compressed = await compressImage(e.dataTransfer.files[0]);
                setSourceImage(compressed);
              }
            }}>
            <div style={{ opacity: .4 }}><Icons.Upload /></div>
            <div>Click or Drop Image</div>
          </div>
          {sourceImage && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '8px 12px', borderRadius: 8 }}>
              <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0, marginRight: 8 }}>
                <img src={safeImgSrc(sourceImage)} alt="source" style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover' }} />
                <button onClick={(e) => { e.stopPropagation(); setSourceImage(null); setRotateX(0); setRotateY(0); setDistance(1); setWideAngle(false); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>✕</button>
              </div>
              <span style={{ fontSize: 12, color: 'var(--t2)' }}>Image Ready</span>
            </div>
          )}
        </div>


        {/* Angle Sliders */}
        <div className="psec">
          <div className="slbl">Manual Adjustments</div>

          <FuturisticSlider
            label="Left - Right"
            min={-90} max={90} step={15} value={rotateY}
            onChange={setRotateY}
            onReset={() => setRotateY(0)}
          />

          <FuturisticSlider
            label="Vertical Angle (Bird ⬄ Worm)"
            min={-1} max={1} step={0.1} value={rotateX}
            onChange={(v) => setRotateX(parseFloat(v.toFixed(1)))}
            onReset={() => setRotateX(0)}
          />

          <FuturisticSlider
            label="Move Forward → Close-Up"
            min={1} max={10} step={0.5} value={distance}
            onChange={setDistance}
            onReset={() => setDistance(1)}
          />

          {/* WIDE-ANGLE LENS */}
          <div style={{ marginBottom: 16 }}>
            <div className="trow" style={{ marginTop: 0 }}>
              <span className="tlbl" style={{ fontSize: 11 }}>Wide-Angle Lens</span>
              <div className={`tog ${wideAngle ? 'on' : ''}`} onClick={() => setWideAngle(!wideAngle)}><div className="togk" /></div>
            </div>
          </div>

          <button
            className="f-reset-btn"
            onClick={() => { setRotateX(0); setRotateY(0); setDistance(1); setWideAngle(false); }}
            title="Reset All Attributes"
          >
            <Icons.Refresh sz={14} /> Reset All Attributes
          </button>
        </div>

        {/* Generate Button */}
        <div className="gen-sticky">
          <div className="cost-row">
            <span className="cost-l">Cost</span>
            <span className="cost-v">
              {cost} cr {' → '}{Math.max(0, (credits || 0) - cost)} left
            </span>
          </div>
          <button className="gbtn" onClick={handleGen} disabled={genLockRef.current || !sourceImage || credits < cost}>
            <Icons.Sparkle sz={15} /> Generate Angle
          </button>
        </div>
      </section>

      {/* ── Main Gallery Area ── */}
      <div className={`main ${showMobilePreview ? 'mobile-preview-open' : ''}`}>
        <div className="mtbar">
          <button className="mtab main-back-mobile" onClick={() => setShowMobilePreview(false)}>
            <Icons.X sz={13} /> Close
          </button>
          <div className="mtab on">Generated Angles</div>
        </div>

        <div className="mbody">

          {(localJobs.length > 0 || recentImages.length > 0) && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Grid sz={13} /> {localJobs.length > 0 ? 'Generating...' : 'Recent Shots'}
              </div>
              <div className="ggrid" style={{ marginBottom: 24 }}>
                {/* Pending shimmer cards */}
                {localJobs.map(j => (
                  <div key={j.id} className="gitem-pending">
                    <div className="pend-icon"><Icons.Orbit sz={28} /></div>
                    <div className="pend-label">{j.prompt || 'Calculating Environment...'}</div>
                    <div className="pend-model">{j.model}</div>
                    <div className="pend-bar-wrap"><div className="pend-bar" /></div>
                    <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 10, textAlign: 'center' }}>Estimated time 4 to 10 min</div>
                  </div>
                ))}
              </div>

              <div className="grouped-collections">
                {groupItemsByDate(recentImages).map(group => (
                  <div key={group.label} className="date-group-wrap">
                    <div className="date-group-label" style={{ marginBottom: 12 }}>{group.label}</div>
                    <div className="ggrid">
                      {group.items.map(i => (
                        <div key={i.id} className="gitem" onClick={() => setLbItem(i)}>
                          <img src={i.imageUrl} alt="" />
                          <div className="gbadge" style={{ background: 'rgba(139,92,246,.12)', color: '#8b5cf6' }}><Icons.Orbit sz={11} /></div>
                          <div className="gact" style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, zIndex: 15, opacity: 0, transition: 'opacity 0.2s' }}>
                            <button onClick={(e) => { e.stopPropagation(); import('../utils/downloadMedia').then(m => m.downloadMedia(i.videoUrl || i.imageUrl, i.type, token)); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#fff', display: 'flex' }}><Icons.DL sz={14} /></button>
                            <button onClick={(e) => { e.stopPropagation(); if (typeof handleDeleteItem === 'function') handleDeleteItem(i.id || i.$id); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#ef4444', display: 'flex' }}><Icons.Trash sz={14} /></button>
                          </div>
                          <div className="gov">
                            <div className="gm">
                              <div style={{ fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{i.prompt || 'Different Angles'}</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{i.modelName || i.model}</span><span>{i.creditsUsed || 0} cr</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <Lightbox item={lbItem} onClose={() => setLbItem(null)} onDelete={handleDeleteItem} />
      <MediaLibraryModal
        open={libOpen}
        onClose={() => setLibOpen(false)}
        allowedTypes={['image']}
        maxSelect={1}
        maxTypeCounts={{ image: 1, video: 0, audio: 0 }}
        onSelect={(assets) => {
          setSourceImage(assets[0]);
        }}
      />
    </>
  );
}


